import { Feed, NoMoreFeedItemsCallbackProp } from "@components/feed";
import { MAX_COLS } from "@components/layout/MasonryList";
import { PageSection } from "@components/layout/PageSection";
import { useCallback } from "react";

import {
  FeedItemResponse,
  UserResponse,
} from "@every.org/common/src/codecs/entities";
import { FeedItemType, FeedPage } from "@every.org/common/src/entity/types";
import {
  getRoutePath,
  URLFormat,
  ClientRouteName,
} from "@every.org/common/src/helpers/clientRoutes";

import { Link } from "src/components/Link";
import { useLoggedInUserOrUndefined } from "src/context/AuthContext/hooks";
import { fetchGiftsFeed } from "src/context/DonationsContext/actions";
import { LoadImageWithPriorityProvider } from "src/context/LoadImageWithPriorityContext/LoadImageWithPriorityContext";

interface GiftsTabProps extends NoMoreFeedItemsCallbackProp {
  user: UserResponse;
  donationsFeed?: FeedItemResponse[];
}
export const GiftsTab = ({
  user,
  onNoMoreItems,
  donationsFeed,
}: GiftsTabProps) => {
  const loggedInUser = useLoggedInUserOrUndefined();
  const isOwnProfile = user.id === loggedInUser?.id;

  return (
    <GiftsFeed
      userId={user.id}
      isOwnFeed={isOwnProfile}
      name={user.firstName}
      onNoMoreItems={onNoMoreItems}
      donationsFeed={donationsFeed}
    />
  );
};

interface GiftsFeedProps extends NoMoreFeedItemsCallbackProp {
  userId: UserResponse["id"];
  name?: string | null;
  isOwnFeed: boolean;
  donationsFeed?: FeedItemResponse[];
}

/**
 * Feed of donations from a given user.
 */
const GiftsFeed = ({
  userId,
  name,
  isOwnFeed,
  onNoMoreItems,
  donationsFeed,
}: GiftsFeedProps) => {
  const noItemsDisplay = (
    <h3>
      {isOwnFeed ? (
        <span>
          {"You haven't publicly supported any nonprofits yet. "}
          <Link
            data-tname="UserRecentDonationsDonateLink"
            to={getRoutePath({
              format: URLFormat.RELATIVE,
              name: ClientRouteName.HOME,
            })}
          >
            Find your first!
          </Link>
        </span>
      ) : (
        `${name || "This person"} hasn't publicly supported any nonprofits yet.`
      )}
    </h3>
  );

  const itemsIdWithPriority = donationsFeed
    ? donationsFeed
        ?.filter((item) => item.type === FeedItemType.USER_DONATION)
        .slice(0, Math.min(donationsFeed.length, MAX_COLS))
        // If again to narrow the type
        .map((item) =>
          item.type === FeedItemType.USER_DONATION
            ? (item.donationCharge.toNonprofitId as string)
            : ""
        )
    : [];

  return (
    <PageSection>
      <LoadImageWithPriorityProvider
        initialValue={{ ids: itemsIdWithPriority }}
      >
        <Feed
          page={FeedPage.USER_PROFILE}
          loadMoreItems={useCallback(
            (params) => fetchGiftsFeed({ userId, ...params }),
            [userId]
          )}
          noItemsDisplay={noItemsDisplay}
          onNoMoreItems={onNoMoreItems}
          initialItems={donationsFeed}
        />
      </LoadImageWithPriorityProvider>
    </PageSection>
  );
};
